import { Box, Collapse, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ChevronUp, GraduationCap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import AccordionActivity from "./AccordionActivity.component";
import {
  AccordionHeader,
  AccordionWrapper,
  CoverImage,
  HeaderContent,
  HeaderRight,
  MetaTypography,
  TitleTypography
} from "./PathCourseAccordion.styles";
import type { PathCourseAccordionProps } from "./PathCourseAccordion.types";
import { ProgressIndexCircle } from "./ProgressIndexCircle.component";
import { handleBorderColor } from "./utils";
import { LEVEL } from "../../consts";
import { useLevel, useType } from "../../hooks";
import { useLearningObjectQuery } from "../../queries";
import { useNavigate } from "../../services";
import { formatDuration_h_m, hasPassedExpiration } from "../../utils/datetime";
import { StatusBoxesContainer } from "../CarouselCardRestyle/CarouselCard.style";
import { type ActivityItem } from "../CourseActivityCard";

export function PathCourseAccordion({
  course,
  ecmRegistration = true,
  ecm_specialization = false,
  index,
  rootId,
  //pathActivities,
  setOpenModalEcm,
  defaultOpen = false,
  initialActiveLoId
}: PathCourseAccordionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  
  const handleToggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (rootId) {
      const isForYou = window.location.href.includes("per-te");
      navigate(
        willOpen
          ? navigate.getDetailLink({ id: course.id, isForYou, learningObjectTypology: LEVEL.COURSE, rootId })
          : navigate.getDetailLink({ id: rootId, isForYou, learningObjectTypology: LEVEL.PATH }),
        { replace: true, state: { preserveScroll: true } }
      );
    }
  };

  const { data: courseDetail, isLoading } = useLearningObjectQuery({
    enabled: isOpen,
    id: course.id,
    learningObjectTypology: LEVEL.COURSE,
    rootId: rootId ?? null
  });

  const levelLabel = useLevel(course.learningObjectTypology);
  const typeLabel = useType(course.learningObjectType, course.learningObjectTypology);
  const isCompleted = course.status === "C";
  const isExpired = hasPassedExpiration(course.expirationDate);
  const isEcmDisabled = !ecmRegistration && ecm_specialization;

  const rawActivities =
    courseDetail && "learningActivities" in courseDetail
      ? courseDetail.learningActivities
      : [];

  const courseActivities: ActivityItem[] = (rawActivities ?? []).map((activity) => ({
    attributes: activity.attributes,
    badge: activity.badge,
    canAccessNow: course.canAccessNow,
    certificate: activity.certificate,
    courseId: course.id,
    disabled: !activity.isEnable || isEcmDisabled,
    duration: activity.duration,
    ecmRegistration: false,
    ecm_specialization: false,
    editions: activity.editions,
    enrollType: activity.enrollType,
    expirationDate: activity.expirationDate,
    externalFileUrl: activity.externalFileUrl,
    grandParentId: activity.grandParentId ?? undefined,
    id: activity.id,
    isEditorial: activity.isEditorial,
    isFavourite: activity.isFavourite,
    isLinkRelative: !!activity.isLinkRelative,
    isMandatory: activity.isMandatory,
    isStandAlone: activity.isStandAlone,
    isSurvey: activity.isSurvey,
    isTest: activity.isTest,
    learningObjectType: activity.learningObjectType,
    learningObjectTypology: activity.learningObjectTypology,
    link: activity.link,
    madeAttempts: activity.madeAttempts ?? undefined,
    maxAttempts: activity.maxAttempts,
    parentId: activity.parentId ?? undefined,
    percentageOfCompletion: activity.percentageOfCompletion,
    rootId: rootId,
    setOpenModalEcm: setOpenModalEcm,
    status: activity.status || "N",
    teachers: activity.teachers,
    title: activity.title,
    toolNumber: activity.toolNumber
  }));

  const effectiveTypeLabel = course.isTest
    ? t("test")
    : course.isSurvey
      ? t("survey")
      : typeLabel;

  const completionPercentage = Number.parseFloat(
    course.percentageOfCompletion?.replace("%", "") ?? "0"
  );
  const progressValue = Number.isFinite(completionPercentage) ? completionPercentage : 0;

  const buildMetaTypography = () => {
    const icon = <GraduationCap size={ "1rem" }/>;
    const duration = formatDuration_h_m(course.duration) ? `${formatDuration_h_m(course.duration)}` : "";
    const numberActivities = course.learningActivityNumber > 0
      ? <>
        { icon }{ " " }{ t("activity", { count: course.learningActivityNumber }) }
      </>
      : null;

    const metaParts = [
      levelLabel,
      effectiveTypeLabel,
      duration
    ].filter(Boolean).join(" | ");

    return (
      <Stack
        flexDirection={ "row" }
        alignItems={ "center" }
        gap={ "1rem" }
        flexWrap={ "wrap" }
      >
        <MetaTypography>
          { metaParts }
        </MetaTypography>
        <MetaTypography
          sx={ {
            gap:"0.25rem"
          } }
        >
          { numberActivities }
        </MetaTypography>
      </Stack>
    );
  };
  // Aria label for accessibility (same logic as CarouselCardRestyle)

  let ariaLabel = `${course.title}. ${t("typology_carousel")}:${t("course")}`;

  if (course.duration) {
    ariaLabel = `${ariaLabel}. ${t("duration")}:${formatDuration_h_m(course.duration)}`;
  }

  if (isCompleted) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("completed")}`;
  } else if (isExpired) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("expired")}`;
  } else if (course.isMandatory) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("mandatory")}`;
  }

  if(course.learningActivityNumber === 1 ){
    ariaLabel = `${ariaLabel}. ${t("contains")}:${t("activity_one",{ count:1 })}`;
  }else if(course.learningActivityNumber > 1){
    ariaLabel = `${ariaLabel}. ${t("contains")}:${t("activity_other",{ count:course.learningActivityNumber })}`;
  }

  const accordionPanelId = `path-course-accordion-${course.id}-panel`;
  return (
    <AccordionWrapper
      id={ `path-course-accordion-${course.id}` }
      data-testid="path-course-accordion-root"
      tabIndex={ -1 }
      border={ `0.0625rem solid ${handleBorderColor(isCompleted, isOpen, theme)}` }
    >
      <AccordionHeader
        id={ `path-course-accordion-${course.id}-header` }
        data-testid="path-course-accordion-header"
        tabIndex={ 0 }
        aria-label={ ariaLabel }
        aria-controls={ accordionPanelId }
        role="button"
        aria-expanded={ isOpen }
        onClick={ handleToggle }
        onKeyDown={ (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        } }
        sx={ {
          "&:focus-visible": {
            outline: `2px solid ${theme.customColors.headerIcon}`,
            outlineOffset: "-3px",
            borderRadius: theme.spacing(2)
          }
        } }
        position={ "relative" }
        flexWrap={ "wrap" }
      >
        <Stack
          id="header-left"
          flexDirection={ "row" }
          gap={ "1rem" }
          flexGrow={ 1 }
          flexWrap={ "wrap" }
          marginRight={ "1rem" }
        >
          <ProgressIndexCircle
            label={ index }
            value={ progressValue }
            isCompleted={ isCompleted }
          />


          { course.coverPublicURL && (
            <CoverImage
              alt={ course.title }
              src={ course.coverPublicURL }
              onClick={ (e) => e.stopPropagation() }
            />
          ) }

          <HeaderContent
            id={ `header-title-and-meta-${course.id}` }
            minWidth={ "fit-content" }
          >
            <TitleTypography>{ course.title }</TitleTypography>

            { buildMetaTypography() }

          </HeaderContent>
        </Stack>

        <HeaderRight
          id="header-right"
          sx={ {
            margin: "0rem 1.5rem 0rem auto"
          } }
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={ 0.5 } >
            <StatusBoxesContainer
              attributes={ course.attributes }
              endDate={ undefined }
              expirationDate={ course.expirationDate }
              id={ course.id }
              isCompleted={ isCompleted }
              isExpired={ isExpired }
              isMandatory={ course.isMandatory }
              isSurvey={ course.isSurvey }
              isTest={ course.isTest }
              learningObjectTypology={ course.learningObjectTypology }
              startDate={ undefined }
            />
          </Box>
        </HeaderRight>

        <Box
          sx={ {
            position: "absolute",
            right: "0.75rem",
            top: "50%",
            transform: "translateY(-50%)" ,
            display: "flex",
            flexShrink: 0
          } }
        >
          { isOpen
            ? <ChevronUp size={ 20 } color={ theme.customColors.textPrimary } />
            : <ChevronDown size={ 20 } color={ theme.customColors.textPrimary } />
          }
        </Box>
      </AccordionHeader>

      <Collapse
        in={ isOpen }
        timeout={ 300 }
        id={ accordionPanelId }
        data-testid="path-course-accordion-panel"
        role="region"
        aria-labelledby={ `path-course-accordion-${course.id}-header` }
      >
        <AccordionActivity
          course={ course }
          isLoading={ isLoading }
          activities={ courseActivities }
          initialActiveLoId={ initialActiveLoId }
        />
      </Collapse>
    </AccordionWrapper>
  );
}
